import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RootState } from '../store';
import { setAIRecommendations } from '../store/dashboardSlice';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks, Breakpoints } from '../constants/theme';
import { AIRecommendation } from '../types';
import { AccessibilityUtils } from '../utils/accessibility';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import { filterAIRecommendations, hasPermission } from '../utils/roleBasedAccess';

// Ultra-Modern Professional Canvas
const ScreenContainer = styled.div`
  background: ${Colors.background};
  min-height: 100vh;
  position: relative;
  padding: 0;
  overflow-x: hidden;
  font-family: ${FontStacks.primary};
  
  /* Professional container layout */
  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: ${Spacing.xxxl}px ${Spacing.xl}px;
  }
`;


const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xxxxl}px;

  @media (max-width: ${Breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacing.lg}px;
  }

  @media (max-width: ${Breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  text-align: center;
  transition: all 200ms ease;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
    transform: translateY(-2px);
  }
`;

const StatValue = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.sm}px;
  line-height: 0.9;
  letter-spacing: -0.02em;
`;

const StatLabel = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${Spacing.xxxl}px;
  padding-bottom: ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
`;

const SectionTitle = styled.h2`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.display}px;
  font-weight: ${FontWeights.semibold};
  margin: 0;
  color: ${Colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.1;
  
  /* Ultra-clean underline */
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${Colors.secondary};
    border-radius: 1px;
  }
`;

const RecommendationsContainer = styled.div`
  display: grid;
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xxxxl}px;
`;

const RecommendationCard = styled.div<{ priority: string }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-left: 4px solid ${props => 
    props.priority === 'HIGH' ? Colors.danger :
    props.priority === 'MEDIUM' ? Colors.warning :
    Colors.success
  };
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  transition: all 200ms ease;
  position: relative;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${props => 
      props.priority === 'HIGH' ? Colors.danger :
      props.priority === 'MEDIUM' ? Colors.warning :
      Colors.success
    };
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.xl}px;

  @media (max-width: ${Breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${Spacing.md}px;
  }
`;

const TypeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const TypeIcon = styled.div<{ type: string }>`
  width: 32px;
  height: 32px;
  border-radius: ${BorderRadius.md}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.bold};
  background: ${props => 
    props.type === 'RISK' ? Colors.danger + '20' :
    props.type === 'SUCCESSION' ? Colors.warning + '20' :
    props.type === 'PROMOTION' ? Colors.success + '20' :
    props.type === 'HIRING' ? Colors.info + '20' :
    Colors.secondary + '20'
  };
  color: ${props => 
    props.type === 'RISK' ? Colors.danger :
    props.type === 'SUCCESSION' ? Colors.warning :
    props.type === 'PROMOTION' ? Colors.success :
    props.type === 'HIRING' ? Colors.info :
    Colors.secondary
  };
`;

const TypeText = styled.span`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  font-weight: ${FontWeights.medium};
  text-transform: capitalize;
`;

const PriorityBadge = styled.span<{ priority: string }>`
  background: ${props => 
    props.priority === 'HIGH' ? Colors.danger + '20' :
    props.priority === 'MEDIUM' ? Colors.warning + '20' :
    Colors.success + '20'
  };
  color: ${props => 
    props.priority === 'HIGH' ? Colors.danger :
    props.priority === 'MEDIUM' ? Colors.warning :
    Colors.success
  };
  padding: ${Spacing.xs}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecommendationTitle = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.lg}px 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const RecommendationDescription = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  line-height: 1.6;
  margin: 0 0 ${Spacing.xl}px 0;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xl}px;
  
  @media (max-width: ${Breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${Spacing.lg}px;
  }
`;

const ActionContainer = styled.div`
  margin-bottom: ${Spacing.lg}px;
`;

const ActionLabel = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-bottom: ${Spacing.sm}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${FontWeights.medium};
`;

const ActionText = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textPrimary};
  line-height: 1.5;
  margin: 0;
  font-weight: ${FontWeights.medium};
`;

const ImpactContainer = styled.div`
  margin-bottom: ${Spacing.lg}px;
`;

const ImpactLabel = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-bottom: ${Spacing.sm}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${FontWeights.medium};
`;

const ImpactText = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textPrimary};
  line-height: 1.5;
  margin: 0;
  font-weight: ${FontWeights.medium};
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: ${Spacing.xl}px;
  padding-top: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.border};
  
  @media (max-width: ${Breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${Spacing.md}px;
  }
`;

const ConfidenceContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const ConfidenceLabel = styled.span`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-right: ${Spacing.md}px;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 80px;
`;

const ConfidenceBar = styled.div`
  flex: 1;
  height: 8px;
  background-color: ${Colors.borderLight};
  border-radius: ${BorderRadius.sm}px;
  overflow: hidden;
  margin-right: ${Spacing.md}px;
  max-width: 120px;
`;

const ConfidenceProgress = styled.div<{ confidence: number }>`
  height: 100%;
  width: ${props => props.confidence}%;
  background: linear-gradient(90deg, ${Colors.success}, ${Colors.secondary});
  border-radius: ${BorderRadius.sm}px;
  transition: width 0.6s ease;
`;

const ConfidenceValue = styled.span`
  font-family: ${FontStacks.mono};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  min-width: 45px;
`;

const AffectedCount = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  font-weight: ${FontWeights.medium};
  text-align: right;
`;

const ViewDetailsButton = styled.button`
  background: transparent;
  border: 1px solid ${Colors.border};
  color: ${Colors.secondary};
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  transition: all 150ms ease;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  
  &:hover {
    background: ${Colors.secondary};
    color: ${Colors.textOnPrimary};
    border-color: ${Colors.secondary};
    transform: translateY(-1px);
    box-shadow: ${Shadows.sm};
  }
  
  &:focus {
    outline: none;
    background: ${Colors.surfaceSelected};
    border-color: ${Colors.borderFocus};
    color: ${Colors.secondary};
    box-shadow: 0 0 0 3px ${Colors.borderFocus}20;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TalentInsightsScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentRole } = useSelector((state: RootState) => state.user);
  const { aiRecommendations } = useSelector((state: RootState) => state.dashboard);
  const [selectedRecommendation, setSelectedRecommendation] = useState<AIRecommendation | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Check if user has permission to view AI insights
  const canViewAIInsights = hasPermission(currentRole, 'canViewAIInsights');

  useEffect(() => {
    loadAIRecommendations();
  }, []);

  const loadAIRecommendations = () => {
    const mockRecommendations: AIRecommendation[] = [
      {
        id: '1',
        type: 'RISK',
        priority: 'HIGH',
        title: 'High Attrition Risk - Engineering Team',
        description: '5 senior engineers showing indicators of potential departure within 3 months',
        suggestedAction: 'Schedule 1-on-1 meetings and review compensation packages',
        impact: 'Potential productivity loss of 40% in critical projects',
        confidence: 94.2,
        affectedEmployees: ['EMP001', 'EMP002', 'EMP003', 'EMP004', 'EMP005'],
      },
      {
        id: '2',
        type: 'SUCCESSION',
        priority: 'HIGH',
        title: 'VP Sales Succession Gap',
        description: 'Only 1 ready candidate identified for critical leadership role',
        suggestedAction: 'Accelerate development programs for 2-3 director-level candidates',
        impact: 'Business continuity risk if current VP departs',
        confidence: 91.7,
      },
      {
        id: '3',
        type: 'PROMOTION',
        priority: 'MEDIUM',
        title: 'Ready for Promotion - 12 Employees',
        description: 'AI analysis identifies employees exceeding role requirements',
        suggestedAction: 'Review promotion eligibility and create advancement plan',
        impact: 'Improved retention and engagement scores',
        confidence: 88.3,
      },
      {
        id: '4',
        type: 'HIRING',
        priority: 'MEDIUM',
        title: 'Optimize Hiring Pipeline',
        description: 'Current screening process missing 30% of qualified candidates',
        suggestedAction: 'Adjust ATS filters and expand sourcing channels',
        impact: 'Reduce time-to-fill by estimated 25%',
        confidence: 85.6,
      },
      {
        id: '5',
        type: 'DEVELOPMENT',
        priority: 'LOW',
        title: 'Leadership Skills Gap Analysis',
        description: '43 mid-level managers need leadership development',
        suggestedAction: 'Implement targeted leadership training program',
        impact: 'Strengthen succession pipeline and team performance',
        confidence: 89.3,
      },
    ];

    // Apply role-based filtering
    const filteredRecommendations = filterAIRecommendations(mockRecommendations, currentRole);
    dispatch(setAIRecommendations(filteredRecommendations));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'RISK': return '⚠';
      case 'SUCCESSION': return '👑';
      case 'PROMOTION': return '↗';
      case 'HIRING': return '🎯';
      case 'DEVELOPMENT': return '📈';
      default: return '💡';
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    AccessibilityUtils.announce('Refreshing insights data');
    setTimeout(() => {
      loadAIRecommendations();
      setRefreshing(false);
      AccessibilityUtils.announce('Insights data updated');
    }, 1500);
  };

  const handleViewDetails = (recommendation: AIRecommendation) => {
    setSelectedRecommendation(recommendation);
    AccessibilityUtils.announce(`Viewing details for ${recommendation.title}`);
    // Modal implementation would go here
    console.log('View details for:', recommendation);
  };

  // Show access denied message for users without AI insights permission
  if (!canViewAIInsights) {
    return (
      <ScreenContainer>
        <div className="container">
          <UnifiedPageHeader
            title={t('insights.title')}
            subtitle="Access Restricted"
            metadata={{
              timestamp: new Date(),
              count: 0,
              status: 'Access Denied'
            }}
            actions={[]}
          />
          
          <div style={{
            background: Colors.surface,
            border: `1px solid ${Colors.border}`,
            borderRadius: `${BorderRadius.lg}px`,
            padding: `${Spacing.xxxxl}px`,
            textAlign: 'center' as const,
            marginTop: `${Spacing.xxxxl}px`
          }}>
            <div style={{ fontSize: '64px', marginBottom: `${Spacing.xl}px`, opacity: 0.7 }}>
              🔒
            </div>
            <h2 style={{
              fontFamily: FontStacks.primary,
              fontSize: `${FontSizes.xxl}px`,
              fontWeight: FontWeights.semibold,
              color: Colors.textPrimary,
              margin: `0 0 ${Spacing.lg}px 0`
            }}>
              Access Restricted
            </h2>
            <p style={{
              fontFamily: FontStacks.primary,
              fontSize: `${FontSizes.lg}px`,
              color: Colors.textSecondary,
              lineHeight: 1.6,
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              AI-powered talent insights are only available to HR Leaders. Contact your HR department to request access to these advanced analytics features.
            </p>
          </div>
        </div>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <div className="container">
        <UnifiedPageHeader
          title={t('insights.title')}
          subtitle={t('insights.subtitle')}
          metadata={{
            timestamp: new Date(),
            count: aiRecommendations.length,
            status: refreshing ? t('insights.loading') : 'Live'
          }}
          actions={[
            {
              label: refreshing ? 'Refreshing...' : t('dashboard.actions.refresh'),
              onClick: handleRefresh,
              variant: 'ghost',
              icon: '🔄',
              disabled: refreshing,
              loading: refreshing,
              ariaLabel: refreshing ? 'Refreshing insights' : 'Refresh insights'
            },
            {
              label: t('dashboard.actions.export'),
              onClick: () => console.log('Export insights'),
              variant: 'secondary',
              ariaLabel: 'Export insights data'
            }
          ]}
        />

        <StatsContainer>
          <StatCard>
            <StatValue>{aiRecommendations.length}</StatValue>
            <StatLabel>Active Insights</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>
              {aiRecommendations.filter(r => r.priority === 'HIGH').length}
            </StatValue>
            <StatLabel>{t('insights.stats.highPriority')}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>91.5%</StatValue>
            <StatLabel>{t('insights.stats.avgConfidence')}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>
              {aiRecommendations.filter(r => r.affectedEmployees?.length).reduce((acc, r) => acc + (r.affectedEmployees?.length || 0), 0)}
            </StatValue>
            <StatLabel>{t('insights.stats.employeesAffected')}</StatLabel>
          </StatCard>
        </StatsContainer>

        <SectionHeader>
          <SectionTitle>{t('insights.sections.recommendations')}</SectionTitle>
        </SectionHeader>
        
        <RecommendationsContainer>
          {aiRecommendations.map((recommendation) => (
            <RecommendationCard key={recommendation.id} priority={recommendation.priority}>
              <CardHeader>
                <TypeContainer>
                  <TypeIcon type={recommendation.type}>{getTypeIcon(recommendation.type)}</TypeIcon>
                  <TypeText>{t(`insights.types.${recommendation.type}`)}</TypeText>
                </TypeContainer>
                <PriorityBadge priority={recommendation.priority}>
                  {t(`insights.priority.${recommendation.priority}`)} {t('insights.priority.priorityText')}
                </PriorityBadge>
              </CardHeader>

              <RecommendationTitle>{recommendation.title}</RecommendationTitle>
              <RecommendationDescription>
                {recommendation.description}
              </RecommendationDescription>

              <MetaGrid>
                <ActionContainer>
                  <ActionLabel>{t('insights.actions.suggestedAction')}</ActionLabel>
                  <ActionText>{recommendation.suggestedAction}</ActionText>
                </ActionContainer>

                <ImpactContainer>
                  <ImpactLabel>{t('insights.actions.expectedImpact')}</ImpactLabel>
                  <ImpactText>{recommendation.impact}</ImpactText>
                </ImpactContainer>
              </MetaGrid>

              <CardFooter>
                <ConfidenceContainer>
                  <ConfidenceLabel>Confidence</ConfidenceLabel>
                  <ConfidenceBar>
                    <ConfidenceProgress confidence={recommendation.confidence} />
                  </ConfidenceBar>
                  <ConfidenceValue>{recommendation.confidence}%</ConfidenceValue>
                </ConfidenceContainer>
                
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                  {recommendation.affectedEmployees && (
                    <AffectedCount>
                      {t('insights.employeesAffectedCount', { count: recommendation.affectedEmployees.length })}
                    </AffectedCount>
                  )}
                  <ViewDetailsButton 
                    onClick={() => handleViewDetails(recommendation)}
                    aria-label={`View details for ${recommendation.title}`}
                  >
                    {t('insights.actions.viewDetails')} →
                  </ViewDetailsButton>
                </div>
              </CardFooter>
            </RecommendationCard>
          ))}
        </RecommendationsContainer>
      </div>
    </ScreenContainer>
  );
};

export default TalentInsightsScreen;